import { useState } from 'react';
import { s } from '../constants/styles';
import { fmt } from '../utils/date';
import { getDayMeta, getDayExercises, getExerciseHistory, getTrainingDayIndices } from '../utils/workout';
import LineChart from './LineChart';

export default function ExerciseProgressCard({ sessions, data }) {
  const todayDayIndex = new Date().getDay();
  const trainingDays = getTrainingDayIndices(data.dayStructure);
  const defaultDay = trainingDays.includes(todayDayIndex) ? todayDayIndex : trainingDays[0];
  const [activeDayIndex, setActiveDayIndex] = useState(defaultDay);
  const [selectedEx, setSelectedEx] = useState(null);

  const exercises = getDayExercises(activeDayIndex, data.customProgram, data.dayStructure);
  const dayMeta = getDayMeta(activeDayIndex, data.dayStructure);
  const dayColor = dayMeta?.color || "#4f8fff";
  const dayName = dayMeta?.name || "";

  const selectedExObj = exercises.find(e => e.id === selectedEx);
  const hist = selectedExObj ? getExerciseHistory(sessions, selectedEx, 10) : [];

  const strengthPoints = hist.slice().reverse().map(h => {
    const ws = h.sets.filter(s => !s.warmup);
    if (!ws.length) return null;
    const top = ws.reduce((best, st) => st.weight > best.weight ? st : best, ws[0]);
    return { y: top.weight, label: fmt(h.date) };
  }).filter(Boolean);

  const volumePoints = hist.slice().reverse().map(h => {
    const ws = h.sets.filter(s => !s.warmup);
    if (!ws.length) return null;
    return { y: ws.reduce((sum, st) => sum + st.weight * st.reps, 0), label: fmt(h.date) };
  }).filter(Boolean);

  // Session volume for the selected day across all sessions
  const sessionVolumePoints = Object.entries(sessions)
    .filter(([, sess]) => sess.dayIndex === activeDayIndex)
    .sort((a, b) => a[0].localeCompare(b[0]))
    .slice(-10)
    .map(([date, sess]) => {
      const vol = Object.values(sess.exercises || {}).reduce((sum, ex) => {
        return sum + (ex.sets || []).filter(s => !s.warmup).reduce((s2, st) => s2 + st.weight * st.reps, 0);
      }, 0);
      return vol > 0 ? { y: Math.round(vol), label: fmt(date) } : null;
    }).filter(Boolean);

  return (
    <div style={{ ...s.card, padding: 16, marginBottom: 16 }}>
      <div style={{ ...s.label, marginBottom: 10 }}>EXERCISE PROGRESS</div>

      {/* Day selector */}
      <div style={{ display: "flex", gap: 5, marginBottom: 14, overflowX: "auto" }}>
        {trainingDays.map(idx => {
          const day = getDayMeta(idx, data.dayStructure);
          const isActive = activeDayIndex === idx;
          const isToday = idx === todayDayIndex;
          return (
            <button key={idx} onClick={() => { setActiveDayIndex(idx); setSelectedEx(null); }} style={{
              padding: "5px 11px", borderRadius: 8, fontSize: 11, fontWeight: 700,
              fontFamily: "inherit", cursor: "pointer", flexShrink: 0, whiteSpace: "nowrap",
              background: isActive ? `${day.color}22` : "var(--bg-2)",
              color: isActive ? day.color : "var(--text-3)",
              border: isActive ? `1px solid ${day.color}44` : "1px solid rgba(255,255,255,0.05)",
            }}>
              {day.name}{isToday ? " ·" : ""}
            </button>
          );
        })}
      </div>

      {/* Exercise pills */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 16 }}>
        {exercises.map(ex => {
          const isSelected = selectedEx === ex.id;
          const hasData = getExerciseHistory(sessions, ex.id, 2).length >= 2;
          return (
            <button key={ex.id} onClick={() => setSelectedEx(isSelected ? null : ex.id)} style={{
              padding: "6px 12px", borderRadius: 10, fontSize: 12, fontWeight: 700,
              fontFamily: "inherit", cursor: "pointer",
              background: isSelected ? `${dayColor}22` : "var(--bg-2)",
              color: isSelected ? dayColor : hasData ? "var(--text-2)" : "var(--text-3)",
              border: isSelected ? `1px solid ${dayColor}44` : "1px solid rgba(255,255,255,0.05)",
              opacity: hasData ? 1 : 0.6,
            }}>
              {ex.name}
              {hasData && <span style={{ marginLeft: 4, fontSize: 9, opacity: 0.7 }}>●</span>}
            </button>
          );
        })}
      </div>

      {/* Graph area — swaps between session volume and exercise charts */}
      <div className="fade-in" key={selectedEx || "volume"}>
        {selectedEx && selectedExObj ? (
          <>
            <div style={{ fontSize: 14, fontWeight: 800, color: dayColor, marginBottom: 14 }}>{selectedExObj.name}</div>
            {strengthPoints.length < 2 ? (
              <div style={{ textAlign: "center", padding: "16px 0", fontSize: 12, color: "var(--text-3)" }}>
                Log at least 2 {dayName} sessions to see charts
              </div>
            ) : (
              <>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ ...s.label, marginBottom: 6 }}>TOP SET WEIGHT</div>
                  <LineChart points={strengthPoints} color={dayColor} height={80} unit=" lbs" />
                </div>
                {volumePoints.length >= 2 && (
                  <div>
                    <div style={{ ...s.label, marginBottom: 6 }}>SESSION VOLUME</div>
                    <LineChart points={volumePoints} color="#a78bfa" height={80} unit=" lbs" />
                  </div>
                )}
              </>
            )}
          </>
        ) : sessionVolumePoints.length >= 2 ? (
          <>
            <div style={{ ...s.label, marginBottom: 6 }}>SESSION VOLUME</div>
            <LineChart points={sessionVolumePoints} color={dayColor} height={90} unit=" lbs" />
            <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 8 }}>
              Tap an exercise above to see individual lift charts
            </div>
          </>
        ) : (
          <div style={{ fontSize: 12, color: "var(--text-3)", textAlign: "center", padding: "16px 0" }}>
            Log at least 2 {dayName} sessions to see charts
          </div>
        )}
      </div>
    </div>
  );
}
